import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag, Menu, X, ShieldCheck, Home, Users, Images, Clapperboard,
  CalendarDays, Flag, Shield, Shirt, UserPlus, Mail, ScrollText, Lock,
} from "lucide-react";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Home", href: "#home", icon: Home },
  { label: "Chi Siamo", href: "#chi-siamo", icon: Users },
  { label: "Galleria", href: "#galleria", icon: Images },
  { label: "Video", href: "#video", icon: Clapperboard },
  { label: "Eventi", href: "#eventi", icon: CalendarDays },
  { label: "Raduni", href: "#raduni", icon: Flag },
  { label: "Direttivo", href: "#direttivo", icon: Shield },
  { label: "Merch", href: "#merch", icon: Shirt },
  { label: "Ordini Club", href: "#ordini-club", icon: Lock },
  { label: "Diventa Socio", href: "#diventa-socio", icon: UserPlus },
  { label: "Contatti", href: "#contatti", icon: Mail },
];

export default function Header() {
  const { count, setCartOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0A0A0B]/85 border-b border-[#C5A059]/20"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src="/logo-cutout.png"
            alt="Logo Neapolis Free Chapter"
            className="h-12 w-12 object-contain transition-all duration-300 group-hover:[filter:drop-shadow(0_0_12px_rgba(197,160,89,0.6))]"
          />
          <div className="leading-tight hidden sm:block">
            <p className="font-display font-bold text-sm tracking-wide text-[#F4EBE1]">
              NEAPOLIS
            </p>
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#C5A059]">
              FREE CHAPTER
            </p>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              title={l.label}
              aria-label={l.label}
              className="group relative p-2.5 text-[#C0B9B0] hover:text-[#C5A059] transition-colors duration-300"
            >
              <l.icon className="w-[18px] h-[18px]" />
              <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2.5 py-1.5 bg-[#18181C] border border-[#C5A059]/30 font-mono text-[9px] uppercase tracking-[0.2em] text-[#C5A059] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                {l.label}
              </span>
            </a>
          ))}
          <Link
            to="/regolamento"
            data-testid="nav-link-regolamento"
            title="Regolamento"
            aria-label="Regolamento"
            className="group relative p-2.5 text-[#C0B9B0] hover:text-[#C5A059] transition-colors duration-300"
          >
            <ScrollText className="w-[18px] h-[18px]" />
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2.5 py-1.5 bg-[#18181C] border border-[#C5A059]/30 font-mono text-[9px] uppercase tracking-[0.2em] text-[#C5A059] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
              Regolamento
            </span>
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="admin-login-button"
            className="hidden sm:flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-[#78746D] hover:text-[#C5A059] transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            Admin
          </Link>
          <button
            data-testid="cart-toggle-button"
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 px-4 py-2.5 border border-[#C5A059]/40 hover:border-[#C5A059] text-[#F4EBE1] text-xs font-mono uppercase tracking-[0.15em] transition-all duration-300 hover:shadow-[0_0_24px_rgba(197,160,89,0.25)]"
          >
            <ShoppingBag className="w-4 h-4 text-[#C5A059]" />
            <span className="hidden sm:inline">Carrello</span>
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 bg-[#9E1B1B] text-[#F4EBE1] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-[#F4EBE1]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0A0A0B] border-t border-[#C5A059]/20 px-5 py-6 space-y-4">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className="block text-sm uppercase tracking-[0.2em] text-[#C0B9B0] font-mono"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/regolamento"
            data-testid="mobile-nav-link-regolamento"
            onClick={() => setMobileOpen(false)}
            className="block text-sm uppercase tracking-[0.2em] text-[#C0B9B0] font-mono"
          >
            Regolamento
          </Link>
          <Link
            to="/admin"
            data-testid="mobile-admin-link"
            className="flex items-center gap-2 text-sm font-mono uppercase tracking-[0.15em] text-[#C5A059]"
          >
            <ShieldCheck className="w-4 h-4" /> Admin
          </Link>
        </div>
      )}
    </header>
  );
}
